// Sales pipeline kanban: every tenant that carries a sales_stage, grouped into
// the 8 pipeline columns. Drag a card to another column to change its stage
// (PATCH /tenants/:id/sales-stage). Cards link to the tenant/lead detail page.

import { useCallback, useRef, useState, type DragEvent } from "react";
import { Link } from "react-router-dom";
import { listLeads, setSalesStage } from "../lib/api";
import { useApi, useMutation } from "../lib/useApi";
import { formatDateShort } from "../lib/format";
import { SALES_STAGES, isLead, type SalesStage, type TenantRow } from "../lib/types";
import { TopBar } from "../components/Layout";
import { Alert, ErrorState, Loading } from "../components/States";

const STAGE_LABELS: Record<SalesStage, string> = {
  new: "New",
  contacted: "Contacted",
  demo: "Demo",
  contract_sent: "Contract sent",
  signed: "Signed",
  provisioned: "Provisioned",
  won: "Won",
  lost: "Lost",
};

// A stable accent per column so the board reads as a left-to-right funnel.
const STAGE_ACCENT: Record<SalesStage, string> = {
  new: "#6366f1",
  contacted: "#3b82f6",
  demo: "#14b8a6",
  contract_sent: "#eab308",
  signed: "#a855f7",
  provisioned: "#f97316",
  won: "#22c55e",
  lost: "#94a3b8",
};

export function SalesPage() {
  const { data, loading, error, reload } = useApi((signal) => listLeads(signal), []);
  const leads = data?.leads ?? [];
  const stageMut = useMutation(setSalesStage);
  const [dragId, setDragId] = useState<string | null>(null);
  const [movingId, setMovingId] = useState<string | null>(null);

  const move = useCallback(
    async (id: string, stage: SalesStage, current: string) => {
      setDragId(null);
      if (stage === current) return;
      setMovingId(id);
      const ok = await stageMut.run(id, stage);
      setMovingId(null);
      // Only reload on success; on failure the card snaps back and the error
      // banner (below) explains why, instead of a silent no-op.
      if (ok) reload();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stageMut.run, reload],
  );

  return (
    <>
      <TopBar
        crumbs="Sales pipeline"
        actions={
          <button className="btn" onClick={reload} disabled={loading}>
            Refresh
          </button>
        }
      />
      <div className="content">
        <div className="page-head">
          <div>
            <h1>Sales pipeline</h1>
            <div className="sub">
              Every prospect with a sales stage. Drag a card between columns to advance it. Create
              leads from <Link to="/tenants">Tenants → Create tenant → Save as lead</Link>.
            </div>
          </div>
        </div>

        {stageMut.error ? <Alert kind="error">{stageMut.error}</Alert> : null}
        {loading ? (
          <Loading label="Loading pipeline…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : leads.length === 0 ? (
          <div className="card">
            <div className="card-body">
              <div className="state muted">
                No leads in the pipeline yet. Add one from the Tenants page (“Save as lead”), or wait
                for a marketing-site inquiry.
              </div>
            </div>
          </div>
        ) : (
          <div className="sales-board">
            {SALES_STAGES.map((stage) => (
              <StageColumn
                key={stage}
                stage={stage}
                leads={leads.filter((l) => l.sales_stage === stage)}
                dragId={dragId}
                movingId={movingId}
                onDragStart={setDragId}
                onDrop={(id, current) => move(id, stage, current)}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function StageColumn({
  stage,
  leads,
  dragId,
  movingId,
  onDragStart,
  onDrop,
}: {
  stage: SalesStage;
  leads: TenantRow[];
  dragId: string | null;
  movingId: string | null;
  onDragStart: (id: string | null) => void;
  onDrop: (id: string, current: string) => void;
}) {
  const [over, setOver] = useState(false);
  const depth = useRef(0);

  function onDragEnter(e: DragEvent) {
    e.preventDefault();
    depth.current += 1;
    setOver(true);
  }
  function onDragLeave() {
    depth.current = Math.max(0, depth.current - 1);
    if (depth.current === 0) setOver(false);
  }
  function onDragOver(e: DragEvent) {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }
  function handleDrop(e: DragEvent) {
    e.preventDefault();
    depth.current = 0;
    setOver(false);
    const payload = e.dataTransfer.getData("text/plain");
    const [id, current] = payload.split("|");
    if (id) onDrop(id, current ?? "");
  }

  return (
    <section
      className={`sales-col${over ? " dropover" : ""}`}
      aria-label={STAGE_LABELS[stage]}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={handleDrop}
    >
      <h3>
        {STAGE_LABELS[stage]}
        <span className="pill">{leads.length}</span>
      </h3>
      {leads.length === 0 ? (
        <div className="sales-empty">—</div>
      ) : (
        leads.map((l) => (
          <LeadCard
            key={l.id}
            lead={l}
            accent={STAGE_ACCENT[stage]}
            dragging={dragId === l.id}
            moving={movingId === l.id}
            onDragState={onDragStart}
          />
        ))
      )}
    </section>
  );
}

function LeadCard({
  lead,
  accent,
  dragging,
  moving,
  onDragState,
}: {
  lead: TenantRow;
  accent: string;
  dragging: boolean;
  moving: boolean;
  onDragState: (id: string | null) => void;
}) {
  return (
    <article
      className={`sales-card${dragging ? " dragging" : ""}${moving ? " busy" : ""}`}
      style={{ ["--card-accent" as string]: accent }}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData("text/plain", `${lead.id}|${lead.sales_stage ?? ""}`);
        e.dataTransfer.effectAllowed = "move";
        onDragState(lead.id);
      }}
      onDragEnd={() => onDragState(null)}
    >
      <Link to={`/tenants/${lead.id}`} className="sales-card-title">
        {lead.name}
      </Link>
      <div className="sales-card-meta">
        <span className="mono">{lead.slug}</span>
        {isLead(lead) ? <span className="pill">lead</span> : <span className="pill ok">live</span>}
      </div>
      {lead.contact_email ? (
        <div className="sales-card-meta">
          <span>{lead.contact_name || lead.contact_email}</span>
        </div>
      ) : null}
      <div className="sales-card-meta">
        <span className="faint">updated {formatDateShort(lead.updated_at)}</span>
      </div>
    </article>
  );
}
